import React from 'react';
import { MenuItem, TextField } from '@material-ui/core';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import { useTranslation } from 'react-i18next';
import { emptyLine, LineDraft, VAT_OPTIONS } from './paymentRequestForm';
import { useFieldIds } from './fieldIds';

const VatField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, error, disabled }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('vat');
  // Chosen explicitly, so typing 10,5 keeps the field open after the 10.
  const [otherRate, setOtherRate] = React.useState(
    !VAT_OPTIONS.includes(value)
  );
  const custom = otherRate || !VAT_OPTIONS.includes(value);
  return (
    <div className="payment-request-vat">
      <TextField
        id={fieldId('vat-rate')}
        select
        size="small"
        variant="outlined"
        label={t('VAT rate')}
        value={custom ? 'custom' : value}
        disabled={disabled}
        onChange={(event) => {
          const other = event.target.value === 'custom';
          setOtherRate(other);
          onChange(other ? '' : String(event.target.value));
        }}
        fullWidth
        InputLabelProps={{ shrink: true }}
      >
        {VAT_OPTIONS.map((rate) => (
          <MenuItem key={rate} value={rate}>
            {rate} %
          </MenuItem>
        ))}
        <MenuItem value="custom">{t('Other rate')}</MenuItem>
      </TextField>
      {custom && (
        <TextField
          id={fieldId('vat-other')}
          size="small"
          variant="outlined"
          label={t('Rate in %')}
          value={value}
          error={error}
          helperText={error ? t('Invalid VAT rate') : undefined}
          disabled={disabled}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(event) => onChange(event.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      )}
    </div>
  );
};

export { VatField };

/** Positions with gross unit price, quantity and VAT rate (Q17). */
const LineEditor: React.FC<{
  lines: LineDraft[];
  errors: Record<string, string>;
  disabled?: boolean;
  onChange: (lines: LineDraft[]) => void;
}> = ({ lines, errors, disabled, onChange }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('line');
  const update = (index: number, patch: Partial<LineDraft>) =>
    onChange(
      lines.map((line, i) => (i === index ? { ...line, ...patch } : line))
    );

  return (
    <div className="payment-request-lines">
      {lines.map((line, index) => (
        <fieldset key={index} className="payment-request-line">
          <legend>{t('Position {{n}}', { n: index + 1 })}</legend>
          <TextField
            id={fieldId(`description-${index}`)}
            size="small"
            variant="outlined"
            label={t('Description')}
            value={line.description}
            error={!!errors[`lines.${index}.description`]}
            helperText={
              errors[`lines.${index}.description`]
                ? t(errors[`lines.${index}.description`])
                : undefined
            }
            disabled={disabled}
            onChange={(event) =>
              update(index, { description: event.target.value })
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <div className="payment-request-line__numbers">
            <TextField
              id={fieldId(`quantity-${index}`)}
              size="small"
              variant="outlined"
              label={t('Quantity')}
              value={line.quantity}
              error={!!errors[`lines.${index}.quantity`]}
              helperText={
                errors[`lines.${index}.quantity`]
                  ? t(errors[`lines.${index}.quantity`])
                  : undefined
              }
              disabled={disabled}
              inputProps={{ inputMode: 'numeric' }}
              onChange={(event) =>
                update(index, { quantity: event.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              id={fieldId(`unit-price-${index}`)}
              size="small"
              variant="outlined"
              label={t('Gross unit price (€)')}
              value={line.unitPrice}
              error={!!errors[`lines.${index}.unitPrice`]}
              helperText={
                errors[`lines.${index}.unitPrice`]
                  ? t(errors[`lines.${index}.unitPrice`])
                  : undefined
              }
              disabled={disabled}
              inputProps={{ inputMode: 'decimal' }}
              onChange={(event) =>
                update(index, { unitPrice: event.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <VatField
              value={line.vatRate}
              error={!!errors[`lines.${index}.vatRate`]}
              disabled={disabled}
              onChange={(vatRate) => update(index, { vatRate })}
            />
          </div>
          {lines.length > 1 && (
            <Button
              variant="transparent"
              color="error"
              translation={null}
              aria-label={t('Remove position {{n}}', { n: index + 1 })}
              disabled={disabled}
              onClick={() => onChange(lines.filter((_, i) => i !== index))}
            >
              {t('Remove')}
            </Button>
          )}
        </fieldset>
      ))}
      <Box>
        <Button
          variant="outlined"
          translation={null}
          disabled={disabled || lines.length >= 50}
          onClick={() => onChange([...lines, emptyLine()])}
        >
          {t('Add position')}
        </Button>
      </Box>
    </div>
  );
};

export default LineEditor;
